import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Typography,
  Button,
  Tag,
  HighlightCards,
  Alert,
  Card,
  Avatar,
  Icon,
  CircularProgress,
} from "@ann/web-components";
import {
  OverviewContainer,
  MetricsContainer,
  GeneralInformation,
  GeneralInformationHeader,
  GeneralInformationContent,
  GeneralInformationColumnItem,
  InfoItemRow,
} from "./Overview.styled";
import { getKnowledgeByUuid } from "../../../../services/knowledge";
import { getUserByUuid } from "../../../../../../../services/users";
import { getDepartmentList } from "../../../../../../../services/departments";
import EditKnowledgeModal from "../../../Main/components/EditKnowledgeModal/EditKnowledgeModal";

const Overview = ({ knowledgeData: propKnowledgeData, loading: propLoading, error: propError }) => {
  const { knowledgeId } = useParams();
  const [knowledgeData, setKnowledgeData] = useState(propKnowledgeData || null);
  const [loading, setLoading] = useState(propLoading || false);
  const [error, setError] = useState(propError || null);
  const [createdByUser, setCreatedByUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [departmentsList, setDepartmentsList] = useState([]);

  // Mock data - fallback when no data is available
  const mockData = {
    created_by: {
      name: "Mike Abbott",
      avatar: "https://placehold.co/32x32/2196F3/FFF?text=MA",
    },
    created_at: "2025-12-12T10:30:00Z",
    updated_at: "2025-11-27T14:20:00Z",
    status: "ACTIVE",
  };

  // Load departments for EditKnowledgeModal
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await getDepartmentList({
          page: 1,
          page_size: 100,
          order_by: "created_at",
          order_direction: "DESC",
        });
        
        if (response.body && Array.isArray(response.body)) {
          const list = response.body.map((dept) => ({
            uuid: dept.department_uuid || dept.departmentUuid,
            name: dept.department_name || dept.departmentName,
          }));
          setDepartmentsList(list);
        }
      } catch (error) {
        console.error("Error loading departments:", error);
      }
    };
    
    fetchDepartments();
  }, []);

  const fetchKnowledgeData = useCallback(async () => {
    if (!knowledgeId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getKnowledgeByUuid({ knowledgebase_uuid: knowledgeId });
      
      if (response.body) {
        // Map API response to component format
        const mappedData = {
          createdBy: response.body.createdBy,
          createdAt: response.body.createdAt,
          updated_at: response.body.lastUpdated,
          created_at: response.body.createdAt,
          status: response.body.status,
          name: response.body.name,
          description: response.body.description,
          department_uuid: response.body.department_uuid,
          owner_uuid: response.body.owner_uuid,
        };

        setKnowledgeData(mappedData);

        // Fetch user information if createdBy is a UUID
        if (response.body.createdBy) {
          try {
            const userResponse = await getUserByUuid(response.body.createdBy);
            if (userResponse.body) {
              setCreatedByUser({
                name: userResponse.body.fullName || userResponse.body.full_name || userResponse.body.name || "Unknown User",
                avatar: userResponse.body.photo_url || userResponse.body.photoUrl || `https://placehold.co/32x32/2196F3/FFF?text=${(userResponse.body.fullName || userResponse.body.full_name || "U").charAt(0).toUpperCase()}`,
              });
            }
          } catch (userError) {
            console.warn("Failed to fetch user data:", userError);
            // Use fallback for createdBy
            setCreatedByUser({
              name: "Unknown User",
              avatar: "https://placehold.co/32x32/2196F3/FFF?text=U",
            });
          }
        }
      }
    } catch (err) {
      console.error("Error fetching knowledge data:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [knowledgeId]);

  useEffect(() => {
    // Only fetch if no prop data is provided
    if (!propKnowledgeData && knowledgeId) {
      fetchKnowledgeData();
    } else if (propKnowledgeData) {
      setKnowledgeData(propKnowledgeData);
    }
  }, [knowledgeId, propKnowledgeData, fetchKnowledgeData]);

  const data = knowledgeData || mockData;
  const createdBy = createdByUser || data.created_by || mockData.created_by;
  const createdAt = data.created_at || data.createdAt || mockData.created_at;
  const updatedAt = data.updated_at || data.lastUpdated || mockData.updated_at;
  const status = data.status || mockData.status;

  const formatDate = (date) => {
    if (!date) return null;
    try {
      return dayjs(date).format("MMM D, YYYY");
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  const handleEditClick = useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  const handleEditSuccess = useCallback(() => {
    // Refresh data after successful edit
    if (knowledgeId) {
      fetchKnowledgeData();
    }
    setIsEditModalOpen(false);
  }, [knowledgeId, fetchKnowledgeData]);

  if (loading && !knowledgeData) {
    return (
      <OverviewContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </div>
      </OverviewContainer>
    );
  }

  if (error && !knowledgeData) {
    return (
      <OverviewContainer>
        <Alert
          title="Error loading knowledge base"
          description={error.message || "An error occurred while loading the knowledge base information."}
          severity="error"
          maxWidth="100%"
          position="relative"
        />
      </OverviewContainer>
    );
  }

  return (
    <OverviewContainer>
      <MetricsContainer>
        <HighlightCards
          color="body"
          feedback="Facts will expire in the next 90 days"
          icon="calendarToday"
          iconSize="medium"
          label="Facts Near Expiration"
          showIcon
          value="45 / 100"
          variant="green"
        />
        <HighlightCards
          color="body"
          feedback="Last 30 days"
          icon="lastUpdated"
          iconSize="medium"
          label="Recent Activity"
          showIcon
          value="3 Changes"
          variant="yellow"
        />
        <HighlightCards
          color="body"
          feedback="Facts approved by an Owner"
          icon="checkCircle"
          iconSize="medium"
          label="Approval Coverage"
          showIcon
          value="65 / 100"
          variant="lightBlue"
        />
      </MetricsContainer>

      <GeneralInformation>
        <GeneralInformationHeader>
          <Typography category="subtitle" size="small" weight="bold" color="subtitle" >
            General Information
          </Typography>
          <Button 
            iconLeft="edit" 
            label="Edit" 
            size="small" 
            type="secondary" 
            pill={true}
            onClick={handleEditClick}
          />
        </GeneralInformationHeader>

        <Card size="medium">
          <GeneralInformationContent>
            <GeneralInformationColumnItem>
              <Typography category="body" size="medium" weight="regular" color="subTitle">
                Created BY
              </Typography>
              <InfoItemRow gapSpacing="8px">
                <Avatar alt={createdBy.name} size="sm" src={createdBy.avatar} />
                <Typography
                  category="body"
                  size="medium"
                  weight="regular"
                  color="body"
                >
                  {createdBy.name}
                </Typography>
              </InfoItemRow>
            </GeneralInformationColumnItem>

            <GeneralInformationColumnItem>
              <Typography category="body" size="medium" weight="regular" color="subTitle">
                Created on
              </Typography>
              <InfoItemRow gapSpacing="8px">
                <Icon name="calendarToday" size="small" />
                <Typography
                  category="body"
                  size="medium"
                  weight="regular"
                  color="body"
                >
                  {formatDate(createdAt)}
                </Typography>
              </InfoItemRow>
            </GeneralInformationColumnItem>

            <GeneralInformationColumnItem>
              <Typography category="body" size="medium" weight="regular" color="subTitle">
                Status
              </Typography>
              <div>
                <Tag
                  color={status === "ACTIVE" ? "success" : "neutral"}
                  onClose={() => {}}
                  size="medium"
                  text={status === "ACTIVE" ? "Active" : "Inactive"}
                />
              </div>
            </GeneralInformationColumnItem>

            <GeneralInformationColumnItem>
              <Typography category="body" size="medium" weight="regular" color="subTitle">
                Last Updated
              </Typography>
              <InfoItemRow gapSpacing="8px">
                <Icon name="calendarToday" size="small" />
                <Typography
                  category="body"
                  size="medium"
                  weight="regular"
                  color="body"
                >
                  {formatDate(updatedAt)}
                </Typography>
              </InfoItemRow>
            </GeneralInformationColumnItem>
          </GeneralInformationContent>
        </Card>
      </GeneralInformation>
        
      <Alert
        title="A Fact is a single, verified statement your agents can trust."
        description="It anchors answers in reliable context (e.g., 'Store hours are 9am-6pm.')."
        severity="info"
        maxWidth="100%"
        position="relative"
        iconPosition="flex-start"
        noShadow={true}
        closable={false}
        zIndex={1}
      />

      <EditKnowledgeModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onSuccess={handleEditSuccess}
        initialData={knowledgeData ? {
          knowledgebase_uuid: knowledgeId,
          name: knowledgeData.name,
          department_uuid: knowledgeData.department_uuid,
          owner_uuid: knowledgeData.owner_uuid,
        } : null}
        departmentsList={departmentsList}
      />
    </OverviewContainer>
  );
};

export default Overview;
